<template>
  <v-container
    ref="energy-content"
    fluid
  >
    <v-row>
      <v-col cols="12">
        <div class="chart-box">
          <div>
            <div class="text-h3">
              Energy Consumption
            </div>
            <span class="font-sm"> Energy Dispensed and Ambient temperature over time</span>
          </div>
          <apexchart
            type="line"
            height="350"
            :options="chartOptions"
            :series="series"
          />
        </div>
      </v-col>
    </v-row>  
    <!--  -->

    <v-row>
      <v-col
        v-for="(row, i) in boxes"
        :key="i"
        cols="12"
        md="4"
        sm="6"
      >
        <div
          class="box"
        >
          <div class="header">
            <span
              class="head-icon"
              :class="`icon-${color[i]}`"
            >
              <v-icon>{{ row.icon }}</v-icon>
            </span>
            <span class="head-title">{{ row.title }}</span>
          </div>
          <div class="value">
            {{ row.value }}
          </div>
          <div class="sub-value">
            {{ row.desc }}
          </div>
        </div>
      </v-col>
    </v-row>
    <!--  -->
  </v-container>
</template>
<script>
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from "moment";
import VueApexCharts from "vue-apexcharts";
import { mapGetters } from "vuex";
export default {
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    item: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      pdfLogo: require("@/assets/brandLogo/pdfLogo.png"),
      color: ["blue", "green", "yellow"],
    };
  },
  computed: {
    ...mapGetters({
      authUser: "getUser",
    }),
    series() {
      const eng = this.item?.energy_consumption_values?.map((r) => r.value);
      const tem = this.item?.energy_consumption_values?.map(() => 25);
      // const energy = [0, 3, 7, 12, 15, 18, 21];
      const energy = eng || [];
      const temperature = tem || [];
      return [
        {
          name: "Energy Dispensed (kWH)",
          type: "area",
          data: energy,
        },
        {
          name: "Ambient Temperature (°C)",
          type: "line",
          data: temperature,
        },
      ];
    },
    chartOptions() {
      const labels = this.item?.energy_consumption_values?.map(
        (r) => r.timestamp
      );

      // const labels =   [
      //   "15:00",
      //   "15:05",
      //   "15:10",
      //   "15:15",
      //   "15:25",
      //   "15:35",
      //   "15:45",
      // ];
      return {
        chart: {
          height: 350,
          type: "line",
          stacked: false,
          toolbar: { show: false },
        },
        stroke: {
          width: [3, 2],
          curve: "smooth",
        },
        // plotOptions: {
        //   bar: {
        //     columnWidth: "50%",
        //   },
        // },

        fill: {
          type: ["solid", "solid"],
          colors: ["#3b82f6", "#22c55e"],
          opacity: [0.2, 1], // Energy filled, temp solid
        },
        colors: ["#3b82f6", "#22c55e"],
        // dataLabels: {
        //   enabled: true,
        //   enabledOnSeries: [1],
        // },

        labels: labels,
        markers: {
          size: 0,
        },
        xaxis: {
          type: "string",
          labels: {
            formatter: function (value) {
              return moment(value).format("hh:mm");
            },
          },
        },
        yaxis: [
          {
            title: {
              text: "Energy Dispensed (kWH)",
            },
          },
          {
            opposite: true,
            title: {
              text: "Ambient Temperature (°C)",
            },
            min: 24,
            max: 26,
            tickAmount: 2,
          },
        ],
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: function (y) {
              if (typeof y !== "undefined") {
                return y;
              }
              return y;
            },
          },
        },
      };
    },
    boxes() {
      return [
        {
          icon: "mdi-lightning-bolt",
          title: "Total Enery Dispensed",
          value: `${this.item?.energy_consumed || 0} kWh`,
          desc: "Energy Delivered during session",
        },
        {
          icon: "mdi-leaf",
          title: "Carbon Offset",
          value: "80 kg",
          desc: "CO₂ emissions saved",
        },
        {
          icon: "mdi-lightning-bolt",
          title: "Charging Time",
          value: this.item?.duration ||0,
          desc: "Total session duration",
        },
      ];
    },
  },
  methods: {
    downloadFile(v) {
      if (v === "pdf") {
        this.downloadPDF();
      }
    },
    async downloadPDF(fName = "Charging Session Detail - Energy Consumption") {
      const el = this.$refs["energy-content"];

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png");

      const imgProps = {
        width: canvas.width,
        height: canvas.height,
      };

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const horizontalPadding = 10; // 10px padding left and right

      // Max allowed width for image considering padding
      const maxImgWidth = pageWidth - horizontalPadding * 2;
      const maxImgHeight = pageHeight - 80; // leave room for logo + title

      // Scale the image
      const ratio = Math.min(
        maxImgWidth / imgProps.width,
        maxImgHeight / imgProps.height
      );
      const imgWidth = imgProps.width * ratio;
      const imgHeight = imgProps.height * ratio;

      const x = horizontalPadding; // 10px from left
      const y = 100; // 60px from top to leave space for logo/title

      // Add logo and title
      if (this.authUser.isAdmin) {
        pdf.addImage(this.pdfLogo, "PNG", pageWidth - 60, 10, 50, 20); // 60 is width + margin
      } else {
        const bLogo = this.authUser?.business_logo;
        pdf.addImage(bLogo, "PNG", pageWidth - 60, 20, 50, 50);
      }

      pdf.setFontSize(20);
      pdf.text(`${fName}`, 14, 50);

      pdf.setFontSize(16);
      pdf.text(`Session #${this.item?.id}`, 15, 70);
      pdf.text(
        `${this.item?.charge_point_id} /${this.item?.connector_id}`,
        15,
        85
      );

      // Add image of the component
      pdf.addImage(imgData, "PNG", x, y, imgWidth, imgHeight);

      pdf.save(`${fName}.pdf`);
    },
  },
};
</script>
<style lang="sass" scoped>
.chart-box
  border: 1px solid lightgray
  border-radius: 5px
  padding: 1rem

.box
    height: 150px
    border: 1px solid lightgray
    border-radius: 5px
    padding: 1rem
    .header
        .icon-blue
            i
                color: #3b82f6 !important
        .icon-yellow
            i
                color: #f59e0b !important
        .icon-green
            i
                color: #22c55e !important
        .head-title
            color: #000
            padding-left: 5px
            font-weight: 600
            font-size: 14px
    .value
        padding-top: 5px
        color: #000
        font-size: 24px
        font-weight: 600
    .sub-value
        padding-top: 5px
        font-size: 14px

.font-sm
  font-size: 14px

.text-h3
  font-weight: 600
</style>
